import { AspectRatio } from '@livecontrol/core-ui';
import cx from 'classnames';
import React, { useCallback, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components';
import { Helper } from './helper';

const Container = styled.div`
  position: relative;
  border-radius: 5%;
  border: 2px solid transparent;

  &.drop-active {
    border: 2px solid var(--primary);
  }
  &.drop-accept {
    border: 2px solid var(--primary);
  }
  &.drop-reject {
    border: 2px solid var(--danger);
  }
`;

const StyledAspect = styled(AspectRatio)`
  border-radius: 5%;
  background-color: rgba(0, 0, 0, 0.02);
  overflow: hidden;

  > div {
    display: flex;
    align-items: center;
    justify-content: center;

    > .placeholder {
      transition: transform 0.3s;
    }
    &:hover {
      > .placeholder {
        transform: scale(1.05);
      }
    }
  }

  &.altBorder {
    border: solid 2px #2e5bff;
  }
`;

const Overlay = {
  container: styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    opacity: 0;
    transition: opacity 500ms ease-in-out;

    &:hover,
    &.uploading {
      opacity: 1;
    }
  `,
  content: styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: rgba(0, 0, 0, 0.6);
    border-radius: 5%;

    &:hover + div {
      opacity: 0.5;
    }
  `,
  close: styled.div`
    &,
    > span {
      position: absolute;
      top: -5px;
      right: -5px;
    }
    z-index: 2;
    &:hover ~ div {
      opacity: 0.5;
    }
  `
};

const VideoContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;

  video {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const AltPreview = styled.div`
  .altBg {
    width: 100%;
    height: 100%;
    background-color: #f7fafc;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
  .previewIconSection {
    width: 54px;
    height: 54px;
    background: rgba(46, 91, 255, 0.1);
    border-radius: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
    .iconSection {
      width: 40px;
      height: 40px;
      background-size: cover;
      background-position: center;
      background-image: url(/assets/icons/Upload-cloud-blue.svg);
    }
  }
  .displayText {
    font-weight: 600;
    font-size: 15px;
    line-height: 18px;
    color: #2e384d;
  }
`;

interface Props {
  src?: string;
  aspectRatio?: AspectRatio.Props['ratio'];
  onUpdate?: (val: { dataSrc: string; file: File; fileType: string }) => void;
  onRemove?: () => void;
  isUploading?: boolean;
  style?: React.CSSProperties;
  className?: string;
  name?: string;
  useAltStyle?: boolean;
  displayPreviewText?: string;
  onMediaClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  acceptedFileTypes?: string;
  fileType?: 'image' | 'video';
  showVideoControls?: boolean;
  canDelete?: boolean;
  pendingCrop?: boolean;
  onCropClick?: () => void;
}

export const PreviewContainer = ({
  src,
  aspectRatio = 1,
  onUpdate,
  onRemove,
  isUploading,
  name = 'Media',
  style,
  className,
  useAltStyle,
  displayPreviewText,
  onMediaClick,
  acceptedFileTypes = 'image/*',
  fileType,
  showVideoControls = false,
  canDelete = true,
  pendingCrop = false,
  onCropClick
}: Props): React.ReactElement => {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [currentFileType, setCurrentFileType] = useState<'image' | 'video'>(fileType || 'image');
  const videoRef = useRef<HTMLVideoElement>(null);
  
  // Detect media type from src if available
  React.useEffect(() => {
    const fetchData = async (src?:string )=>{
      if (src) {
        const isVideoFile = 
          (typeof src === 'string' && 
            (src.includes('video/') || 
             /\.(mp4|webm|ogg|mov)($|\?)/.test(src.toLowerCase()))) ||
          fileType === 'video' || await Helper.isVideoUrl(src);
        setCurrentFileType(isVideoFile ? 'video' : 'image');
      }
    }

    fetchData(src);
  }, [src, fileType]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      acceptedFiles.forEach(async (file: File) => {
        // Handle both image and video files
        const isImage = file.type.startsWith('image');
        const isVideo = file.type.startsWith('video');

        if (!isImage && !isVideo) {
          return;
        }

        // For both image and video, create a blob
        const blob = await Helper.fileToBlob(file);

        if (blob) {
          // Update the current file type
          const newFileType = isVideo ? 'video' : 'image';
          setCurrentFileType(newFileType);

          onUpdate?.({
            dataSrc: blob,
            file,
            fileType: newFileType
          });
        }
      });
    },
    [onUpdate]
  );

  const onRemoveClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
      e.stopPropagation();
      onRemove?.();
    },
    [onRemove]
  );

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    onDrop,
    accept: acceptedFileTypes
  });

  // Determine which icon to show based on accepted file types
  const getPlaceholderIcon = () => {
    if (name === 'Intro' || name === 'Outro') return 'paperclip';
    if (acceptedFileTypes === 'video/*') return 'file-video';
    if (acceptedFileTypes === 'image/*') return 'image';
    return currentFileType === 'video' ? 'file-video' : 'image';
  };

  const renderPreview = () => {
    if (!src) {
      if (useAltStyle) {
        return (
          <AltPreview>
            <div className='altBg'>
              <div className='previewIconSection'>
                <div className='iconSection' />
              </div>
              <div className='displayText mt-16px'>{displayPreviewText || `Upload ${name}`}</div>
            </div>
          </AltPreview>
        );
      } else {
        return (
          <div className='text-center text-tertiary placeholder'>
            <i className={`far fa-${getPlaceholderIcon()} fa-3x`} />
            <div className='mt-8px font-size-12px'>Upload {name}</div>
          </div>
        );
      }
    }

    if (currentFileType === 'video') {
      return (
        <VideoContainer>
          <video 
            ref={videoRef}
            src={src} 
            controls={showVideoControls && isVideoLoaded}
            onLoadedData={() => setIsVideoLoaded(true)}
            style={{ maxWidth: '100%' }}
            onLoadedMetadata={(e) => {
              const video = e.target as HTMLVideoElement;
              if (video) {
                video.currentTime = 3;
              }
            }}
            onContextMenu={(e) => e.preventDefault()}
            loop
            muted
            playsInline
          />
        </VideoContainer>
      );
    }

    // Default image preview
    return (
      <img
        src={src}
        alt={name || 'preview'}
        style={{
          maxWidth: '100%'
        }}
      />
    );
  };

  return (
    <Container
      {...(pendingCrop ? {} : getRootProps())}
      style={{ ...style, pointerEvents: isUploading ? 'none' : 'initial' }}
      className={cx(className, 'cursor-pointer', {
        'drop-active': isDragActive,
        'drop-accept': isDragAccept,
        'drop-reject': isDragReject
      })}
    >
      <StyledAspect ratio={aspectRatio} className={`${useAltStyle ? 'altBorder' : ''}`}>
        {renderPreview()}
      </StyledAspect>
      <Overlay.container
        className={cx({
          'd-none': !src && !isUploading,
          'uploading': isUploading
        })}
      >
        {canDelete && (
          <Overlay.close className={cx({ 'd-none': !src || isUploading })} onClick={onRemoveClick}>
            <span>
              <i className='fas fa-circle font-size-24px' style={{ color: 'white' }} />
            </span>
            <span>
              <i className='far fa-times-circle font-size-24px' style={{ color: 'black' }} />
            </span>
          </Overlay.close>
        )}
        {!pendingCrop ? (
        <Overlay.content className='overflow-hidden d-flex align-items-center justify-content-center' onClick={onMediaClick}>
          <div className='text-center text-white' id='upload'>
            <i className={`far fa-${getPlaceholderIcon()} fa-3x`} />
            <div className='mt-8px font-size-12px'>
              {isUploading ? 'Uploading...' : `Change ${name}`}
            </div>
              <input {...getInputProps()} multiple={false} type='file' accept={acceptedFileTypes} />
          </div>
        </Overlay.content>
        ) :
          <Overlay.content className='overflow-hidden d-flex align-items-center justify-content-center'
            onClick={(e): void => {
                e.stopPropagation();
                onCropClick?.();
              }}>
          <div className='text-center text-white' id=''>
            <i className='far fa-crop-alt' />
            <div className='mt-8px font-size-12px'>
              Crop Image
            </div>
          </div>
        </Overlay.content>
        }
      </Overlay.container>
    </Container>
  );
};
